/**
 * service-payment client. Mirrors:
 *   apps/service-payment/src/modules/payment/payment.controller.ts
 *
 * Surface used by Phase 2b mail screens:
 *   - POST  /v1/payments              — create a payment intent (counter)
 *   - POST  /v1/payments/:id/capture  — capture an authorised payment
 *   - POST  /v1/payments/:id/void     — void a pending intent
 *   - POST  /v1/payments/:id/refunds  — issue a refund
 *   - GET   /v1/payments/:id          — fetch one
 *   - GET   /v1/payments              — paginated list (filtered by clerk/shift)
 *
 * Aggregator endpoints used by Cash Reconciliation, Daily Transaction Summary,
 * and Shift Management (e.g. /v1/payments/reconciliation, /v1/shifts/current,
 * /v1/shifts/close) DO NOT EXIST today — those screens render the UI and surface
 * a DeferredNotice with the gap in the body.
 */
import { env } from '../env';

import { request } from './client';

const baseUrl = (): string => env.servicePaymentBaseUrl;

export type PaymentMethod = 'CASH' | 'CARD' | 'POS' | 'WALLET' | 'TRANSFER';
export type PaymentStatus =
  | 'PENDING'
  | 'AUTHORISED'
  | 'CAPTURED'
  | 'VOIDED'
  | 'REFUNDED'
  | 'FAILED';

/**
 * Payment response from the backend.
 * Mirrors `PaymentResponseDto` in service-payment/src/modules/payment/payment.dto.ts.
 *
 * Note: backend uses `amountAuthorized` (decimal string) and `methodKind`,
 * not `amountMinor` (integer) and `method`.
 */
export interface PaymentSummary {
  readonly id: string;
  readonly paymentNumber: string;
  readonly tenantId: string;
  readonly reference: PaymentReference;
  readonly customerId?: string | null;
  readonly amountAuthorized: string;
  readonly amountCaptured?: string | null;
  readonly amountRefunded: string;
  readonly currency: string;
  readonly methodKind: PaymentMethod;
  readonly methodRef?: string | null;
  readonly status: string;
  readonly failureReason?: string | null;
  readonly initiatedBy: string;
  readonly initiatedAt: string;
  readonly authorizedAt?: string | null;
  readonly capturedAt?: string | null;
  readonly voidedAt?: string | null;
  readonly updatedAt: string;
  readonly version: number;
}

export interface ListPaymentsResponse {
  readonly items: readonly PaymentSummary[];
  readonly page: number;
  readonly pageSize: number;
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
}

export interface ListPaymentsQuery {
  readonly status?: PaymentStatus;
  readonly method?: PaymentMethod;
  readonly from?: string;
  readonly to?: string;
  readonly page?: number;
  readonly pageSize?: number;
}

/**
 * Domain reference linking this payment to a business entity.
 * Mirrors `PaymentReferenceSchema` in service-payment/src/modules/payment/payment.dto.ts.
 */
export interface PaymentReference {
  readonly kind: string;
  readonly id?: string;
  readonly customerId?: string;
  readonly [key: string]: unknown;
}

/**
 * Create-payment request body.
 * Mirrors `AuthorizePaymentRequestSchema` in service-payment/src/modules/payment/payment.dto.ts.
 *   - `amount`     — decimal NGN string, e.g. "500.00"  (NOT an integer minor unit)
 *   - `methodKind` — CASH | CARD | TRANSFER | WALLET | CUSTOMER_CREDIT
 *   - `reference`  — domain link object { kind, id? }
 */
export interface CreatePaymentRequest {
  readonly reference: PaymentReference;
  readonly amount: string;
  readonly currency?: string;
  readonly methodKind: PaymentMethod;
  readonly methodRef?: string;
  readonly customerId?: string;
  readonly idempotencyKey?: string;
}

export async function listPayments(query: ListPaymentsQuery = {}): Promise<ListPaymentsResponse> {
  return request<ListPaymentsResponse>(baseUrl(), '/v1/payments', {
    method: 'GET',
    query: {
      status: query.status,
      method: query.method,
      from: query.from,
      to: query.to,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export async function getPayment(id: string): Promise<PaymentSummary> {
  return request<PaymentSummary>(baseUrl(), `/v1/payments/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

export async function createPayment(dto: CreatePaymentRequest): Promise<PaymentSummary> {
  return request<PaymentSummary>(baseUrl(), '/v1/payments', {
    method: 'POST',
    body: dto,
  });
}

export async function capturePayment(id: string): Promise<PaymentSummary> {
  return request<PaymentSummary>(
    baseUrl(),
    `/v1/payments/${encodeURIComponent(id)}/capture`,
    { method: 'POST', body: {} },
  );
}

export async function voidPayment(id: string): Promise<PaymentSummary> {
  return request<PaymentSummary>(
    baseUrl(),
    `/v1/payments/${encodeURIComponent(id)}/void`,
    { method: 'POST', body: {} },
  );
}

export async function refundPayment(
  id: string,
  amountMinor: number,
  reason?: string,
): Promise<PaymentSummary> {
  return request<PaymentSummary>(
    baseUrl(),
    `/v1/payments/${encodeURIComponent(id)}/refunds`,
    { method: 'POST', body: { amountMinor, reason } },
  );
}
